import React, { useEffect, useRef, useState } from 'react';

interface DropdownProps {
  /** The clickable trigger contents (rendered inside a button). */
  trigger: React.ReactNode;
  /** Classes for the trigger button. */
  triggerClassName?: string;
  align?: 'left' | 'right';
  /** Classes for the floating panel (e.g. a width). */
  panelClassName?: string;
  'aria-label'?: string;
  /** Panel contents. Any click inside closes the dropdown (after the item's handler). */
  children: React.ReactNode;
}

/**
 * Lightweight headless dropdown: a trigger button + a floating panel that closes
 * on outside-click, Escape, or any click inside the panel. Shared across apps so
 * the header user/language menus look and behave identically everywhere.
 */
export const Dropdown: React.FC<DropdownProps> = ({
  trigger, triggerClassName = '', align = 'right', panelClassName = '', children, ...rest
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={rest['aria-label']}
        onClick={() => setOpen((o) => !o)}
        className={triggerClassName}
      >
        {trigger}
      </button>
      {open && (
        <div
          role="menu"
          onClick={() => setOpen(false)}
          className={[
            'absolute z-50 mt-2 overflow-hidden rounded-lg border border-line bg-surface py-1 shadow-xl',
            align === 'right' ? 'right-0' : 'left-0',
            panelClassName,
          ].join(' ')}
        >
          {children}
        </div>
      )}
    </div>
  );
};
